import Base from '../core/Base';
import { request, MODULES } from '../core/Api';
import getUrlVars from '../utilities/getUrlVars';

class Wincode extends Base {
	constructor(props) {
		super(props);

		this.config = {
			isNumeric: true,
			hasDashInCode: true,
			validateWhenLengthMatches: true,
			hideGeneratorIfCodeIsValid: true,
			wincodeFromUrlParams: true,
			hideGeneratorIfWincodeFromUrlParams: true,
			restoreData: {},
			classes: {
				isInputting: 'is-inputting',
				hasPlaceholder: 'has-placeholder',
				hide: 'is-display-none'
			},
			elements: {
				wrapper: '.js-wincode',
				imitation: '.js-wincode-imitation',
				inputText: '.js-wincode-input-text',
				inputNumber: '.js-wincode-input-number',
				generator: '.js-wincode-generator',
				generatorButton: '.js-wincode-generator-button'
			}
		};
	}

	init() {
		const element = document.querySelectorAll(this.config.elements.wrapper);
		if (this.isUnique(element)) {
			this.setState(
				{
					wrapper: element[0]
				},
				() => this.selectElements()
			);
		}
	}

	selectElements() {
		const imitation = this.state.wrapper.querySelector(this.config.elements.imitation);
		const inputText = this.state.wrapper.querySelector(this.config.elements.inputText);
		const inputNumber = this.state.wrapper.querySelector(this.config.elements.inputNumber);
		const generator = this.state.wrapper.querySelector(this.config.elements.generator);
		const generatorButton = this.state.wrapper.querySelector(this.config.elements.generatorButton);

		this.setState(
			{
				imitation,
				inputText,
				inputNumber,
				generator,
				generatorButton
			},
			() => {
				this.prepare();
			}
		);
	}

	prepare() {
		const a = document.createElement('input');

		a.setAttribute('type', 'number');
		a.setAttribute('value', 2319);

		if ('valueAsNumber' in a && a.value != a.valueAsNumber) {
			if ('defineProperty' in Object && 'getPrototypeOf' in Object) {
				Object.defineProperty(Object.getPrototypeOf(a), 'valueAsNumber', {
					get() {
						return parseInt(this.value, 10);
					}
				});
			}
		}

		const { wincode } = getUrlVars();
		const { inputText, inputNumber } = this.state;

		if (wincode && this.config.wincodeFromUrlParams) {
			this.getWincodeFromUrlParams(wincode);
		} else if (inputText.value.length > 0 && inputNumber.value.length > 0) {
			this.getWincodeFromServerPrefill();
		} else {
			this.showPlaceholder();
		}
		this.setEventListeners();
	}

	showPlaceholder() {
		const { placeholder } = this.state.inputText;
		[...placeholder].forEach(symbol => {
			if (symbol === ' ') {
				symbol = '&nbsp;';
			}
			const span = document.createElement('span');
			span.insertAdjacentHTML('beforeEnd', symbol);
			this.state.imitation.insertAdjacentElement('beforeEnd', span);
		});
		this.state.imitation.classList.add(this.config.classes.hasPlaceholder);
	}

	hidePlaceholder() {
		this.state.imitation.innerHTML = '';
		this.state.imitation.classList.remove(this.config.classes.hasPlaceholder);
	}

	getWincodeFromUrlParams(value) {
		const processedValue = value.substring(0, this.state.inputNumber.placeholder.length);

		this.state.inputNumber.value = processedValue;
		this.state.inputText.value = Wincode.createMaskedValue(processedValue);
		this.state.imitation.innerHTML = this.createImitationString(processedValue);

		this.triggerValidation();

		if (this.config.hideGeneratorIfWincodeFromUrlParams) {
			this.hideGenerator();
		}
	}

	getWincodeFromServerPrefill() {
		const { inputNumber } = this.state;

		this.state.imitation.innerHTML = this.createImitationString(inputNumber.value);

		this.triggerValidation();

		if (this.config.hideGeneratorIfWincodeFromUrlParams) {
			this.hideGenerator();
		}
	}

	setEventListeners() {
		this.state.generatorButton.addEventListener('click', event => this.handleGeneratorButtonClick(event));
		this.state.inputNumber.addEventListener('keydown', event => this.handleKeydown(event));
		this.state.inputNumber.addEventListener('keyup', () => this.handleKeyup());

		this.state.inputNumber.addEventListener('focus', () => {
			// Edge focusses at the beginning of the input, so we force the focus to the end:
			const curVal = this.state.inputNumber.value;
			this.state.inputNumber.value = '';
			this.state.inputNumber.value = curVal;
			// Continue as we were
			this.state.imitation.classList.add(this.config.classes.isInputting);
		});
		this.state.inputNumber.addEventListener('blur', () => {
			this.state.imitation.classList.remove(this.config.classes.isInputting);
			this.state.inputText.blur();
		});
	}

	handleKeydown(event) {
		// Return if backspace or tab key is pressed
		if (event.keyCode === 8 || event.keyCode === 9) return;

		if (this.config.isNumeric) {
			// Disable arrow keys
			if (event.keyCode >= 37 && event.keyCode <= 40) {
				event.preventDefault();
			}

			let { key } = event;
			if (key === undefined) {
				key = String.fromCharCode(event.keyCode);
			}
			if (Number.isNaN(Number(key)) || key === 'e') {
				event.preventDefault();
			}
		}
	}

	handleKeyup() {
		const currentValue = this.state.inputNumber.value;

		if (currentValue.length >= 1) {
			this.hidePlaceholder();
		}
		if (this.config.validateWhenLengthMatches && currentValue.length === this.state.inputNumber.placeholder.length) {
			this.triggerValidation();
			if (this.config.hideGeneratorIfCodeIsValid) {
				this.hideGenerator();
			}
		}

		this.state.inputText.value = Wincode.createMaskedValue(currentValue);
		this.state.imitation.innerHTML = this.createImitationString(currentValue);
	}

	triggerValidation() {
		this.state.inputNumber.focus();
		this.state.inputNumber.blur();
	}

	hideGenerator() {
		this.state.generator.classList.add(this.config.classes.hide);
	}

	handleGeneratorButtonClick(event) {
		event.preventDefault();
		request(MODULES.code, this.props.token).then(data => {
			const parsedValue = JSON.parse(data);
			const imitationString = this.createImitationString(parsedValue);
			const maskedValue = Wincode.createMaskedValue(parsedValue);

			this.state.imitation.innerHTML = imitationString;
			this.state.inputText.value = maskedValue;
			this.state.inputNumber.value = parsedValue;
			this.state.inputNumber.focus();
			this.state.inputNumber.blur();

			if (this.state.imitation.classList.contains(this.config.classes.hasPlaceholder)) {
				this.state.imitation.classList.remove(this.config.classes.hasPlaceholder);
			}
			if (this.config.hideGeneratorIfCodeIsValid) {
				this.hideGenerator();
			}
		});
	}

	createImitationString(value) {
		let processedValue = value;
		let imitationString = '<span>&nbsp;</span>';

		if (this.config.hasDashInCode) {
			processedValue = Wincode.createMaskedValue(value);
		}

		[...processedValue].map(item => {
			imitationString += `<span>${item}</span>`;
			return imitationString;
		});

		return imitationString;
	}

	static createMaskedValue(value) {
		const start = value.substring(0, 5);
		const end = value.substring(5, 10);
		const maskedValue = end.length > 0 ? `${start}-${end}` : start;
		return maskedValue;
	}
}

export default Wincode;
